"use client";
import React, {
  useMemo,
  useState,
  useCallback,
  useEffect,
  useRef,
} from "react";
import styles from "./Select.module.scss";
import withLabelAndError from "../withLabelAndError";
import { Controller } from "react-hook-form";
import {
  Autocomplete,
  InputAdornment,
  TextField,
  Box,
  IconButton,
  ListSubheader,
} from "@mui/material";
import { combineClasses } from "@/lib/utils";
import {
  SelectControlProps,
  WithLabelAndErrorComponent,
  Option,
} from "@/types/formControls";
import ArrowIcon from "@/components/SVGComponents/ArrowIcon";
import { FixedSizeList, ListChildComponentProps } from "react-window";

const LISTBOX_PADDING = 8;
const ITEM_SIZE = 34; 

// Virtualized ListboxComponent
const OuterElementContext = React.createContext<Record<string, any>>({});

const InnerElementType = React.forwardRef<HTMLDivElement>(
  function InnerElement(props, ref) {
    return <div ref={ref} {...props} />;
  },
);

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return <div ref={ref} {...props} {...outerProps} />;
});
OuterElementType.displayName = "OuterElementType";

function renderRow(props: ListChildComponentProps) {
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  // Handle grouped options
  if (dataSet && typeof dataSet === "object" && "group" in dataSet) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        {dataSet.group}
      </ListSubheader>
    );
  }

  // Handle array format [props, key, children] from MUI
  if (Array.isArray(dataSet) && dataSet.length >= 3) {
    const [itemProps, itemKey, itemChildren] = dataSet;
    return (
      <div {...(itemProps || {})} key={itemKey} style={inlineStyle}>
        {itemChildren}
      </div>
    );
  }

  // Handle direct React element - clone with new style
  if (React.isValidElement(dataSet)) {
    const elementProps = (dataSet.props || {}) as Record<string, any>;
    // Merge existing style with inline style
    const mergedStyle = {
      ...(elementProps.style || {}),
      ...inlineStyle,
    };
    return React.cloneElement(dataSet, {
      ...elementProps,
      style: mergedStyle,
      key: dataSet.key || index,
    } as any);
  }

  // Fallback
  return (
    <div style={inlineStyle} key={index}>
      {String(dataSet)}
    </div>
  );
}

// Hook to get ref for FixedSizeList
function useResetCache(data: number) {
  const ref = useRef<FixedSizeList>(null);
  useEffect(() => {
    // Reset is not needed for FixedSizeList, but we keep the ref for potential future use
  }, [data]);
  return ref;
}

// Extended option type with metadata for tree structure
interface TreeOption extends Option {
  level: number;
  parentValue?: string | undefined;
  hasChildren: boolean;
  isExpanded?: boolean;
}

// Utility function to build tree structure and get all selectable options
const buildTreeOptions = (
  options: readonly Option[] | Option[] | undefined,
  level: number = 0,
  parentValue?: string,
  expandedSet: Set<string> = new Set(),
): TreeOption[] => {
  if (!options) return [];

  const treeOptions: TreeOption[] = [];

  for (const option of options) {
    const hasChildren = !!(option.children && option.children.length > 0);
    const isExpanded = expandedSet.has(option.value);

    // Add parent option
    const treeOption: TreeOption = {
      ...option,
      level,
      hasChildren,
      isExpanded,
    };
    if (parentValue !== undefined) {
      treeOption.parentValue = parentValue;
    }
    treeOptions.push(treeOption);

    // Add children only if parent is expanded
    if (hasChildren && isExpanded) {
      treeOptions.push(
        ...buildTreeOptions(
          option.children,
          level + 1,
          option.value,
          expandedSet,
        ),
      );
    }
  }

  return treeOptions;
};

// Get all selectable leaf options (for value comparison)
const getAllSelectableOptions = (
  options: readonly Option[] | Option[] | undefined,
): Option[] => {
  if (!options) return [];

  const selectable: Option[] = [];

  for (const option of options) {
    // Include option if it has a value
    if (option.value) {
      selectable.push(option);
    }

    // Recursively get children
    if (option.children && option.children.length > 0) {
      selectable.push(...getAllSelectableOptions(option.children));
    }
  }

  return selectable;
};

// Find all parent option values that contain matching children
const findParentPathsForMatch = (
  options: readonly Option[] | Option[] | undefined,
  searchText: string,
  parentPath: string[] = [],
): string[] => {
  if (!options || !searchText) return [];

  const parentPaths: string[] = [];
  const lowerSearchText = searchText.toLowerCase();

  for (const option of options) {
    const currentPath = [...parentPath, option.value];
    const matchesSearch = option.label.toLowerCase().includes(lowerSearchText);

    // Check if this option or any of its children match
    let hasMatchingChild = false;
    if (option.children && option.children.length > 0) {
      const childMatches = findParentPathsForMatch(
        option.children,
        searchText,
        currentPath,
      );
      if (childMatches.length > 0) {
        hasMatchingChild = true;
        // Add all parent paths from children
        parentPaths.push(...childMatches);
      }
    }

    // If this option matches or has matching children, add all parent paths
    if (matchesSearch || hasMatchingChild) {
      // Add all ancestors to the set
      for (const pathValue of currentPath) {
        if (pathValue) {
          parentPaths.push(pathValue);
        }
      }
    }
  }

  return [...new Set(parentPaths)]; // Remove duplicates
};

const Select: React.FC<SelectControlProps> = ({
  control,
  validations,
  name,
  placeholder,
  isDisabled,
  options,
  optionsHeight,
  error,
  startIcon,
  onChange,
  loader = false,
  isVirtualized = false,
  ...rest
}) => {
  // Track expanded state for collapsible options
  const [expandedSet, setExpandedSet] = useState<Set<string>>(new Set());
  // Track search input to auto-expand matching parents
  const [searchInput, setSearchInput] = useState<string>("");

  // Toggle expand/collapse for a parent option
  const toggleExpand = useCallback(
    (optionValue: string, event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      setExpandedSet((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(optionValue)) {
          newSet.delete(optionValue);
        } else {
          newSet.add(optionValue);
        }
        return newSet;
      });
    },
    [],
  );

  // Auto-expand parents when search matches their children
  React.useEffect(() => {
    if (searchInput.trim()) {
      const parentPaths = findParentPathsForMatch(
        options as Option[] | undefined,
        searchInput,
      );
      if (parentPaths.length > 0) {
        setExpandedSet((prev) => {
          const newSet = new Set(prev);
          parentPaths.forEach((path) => newSet.add(path));
          return newSet;
        });
      }
    }
  }, [searchInput, options]);

  // Build tree options based on expanded state
  const treeOptions = useMemo(() => {
    return buildTreeOptions(
      options as Option[] | undefined,
      0,
      undefined,
      expandedSet,
    );
  }, [options, expandedSet]);

  // eslint-disable-next-line
  const renderInput = (field: any) => {
    // eslint-disable-next-line
    const callerSlotProps = (rest as any).slotProps ?? {};

    // Filter options for multiple select (but include all treeOptions so parent options show)
    const availableOptions = rest?.multiple
      ? treeOptions.filter((opt) => {
          // For multiple select, filter out already selected options
          const val = field?.value?.map((v: { value: string }) => v.value);
          return !val?.includes(opt?.value);
        })
      : treeOptions;

    // Virtualized ListboxComponent - created inline when needed
    let ListboxComponent: React.ComponentType<any> | undefined = undefined;

    if (isVirtualized) {
      const VirtualizedListbox = React.forwardRef<
        HTMLDivElement,
        React.HTMLAttributes<HTMLElement>
      >(function ListboxComponent(props, ref) {
        const { children, ...other } = props as {
          children?: React.ReactNode;
          [key: string]: any;
        };

        // Transform children into the format expected by react-window
        // MUI passes children as React elements, format them as [props, key, children] tuples
        const itemData = React.Children.toArray(children).map(
          (child, index) => {
            if (React.isValidElement(child)) {
              const childProps = (child.props || {}) as Record<string, any>;

              // Handle grouped options (ListSubheader)
              if (
                child.type === ListSubheader ||
                (childProps.component === "div" &&
                  childProps.children &&
                  typeof childProps.children === "string")
              ) {
                return {
                  group: childProps.children,
                  key: child.key || `group-${index}`,
                };
              }

              // Regular list item - format as [props, key, children] tuple
              const propsWithoutStyle: Record<string, any> = {};
              Object.keys(childProps).forEach((key) => {
                if (key !== "style") {
                  propsWithoutStyle[key] = childProps[key];
                }
              });
              return [
                propsWithoutStyle,
                child.key || `item-${index}`,
                childProps.children,
              ];
            }
            return child;
          },
        );

        const itemCount = itemData.length;
        const gridRef = useResetCache(itemCount);

        // Calculate height - handle both number and string (e.g., "300px")
        let height = 200;
        if (optionsHeight) {
          if (typeof optionsHeight === "number") {
            height = optionsHeight;
          } else {
            const parsed = Number.parseInt(
              String(optionsHeight).replace("px", ""),
              10,
            );
            if (!Number.isNaN(parsed)) {
              height = parsed;
            }
          }
        }

        return (
          <div
            ref={ref}
            style={{
              height: `${height}px`,
              width: "100%",
              overflow: "hidden",
              position: "relative",
            }}
            role="listbox"
          >
            <OuterElementContext.Provider value={other}>
              <FixedSizeList
                itemData={itemData}
                height={height}
                width="100%"
                outerElementType={OuterElementType}
                innerElementType={InnerElementType}
                itemSize={ITEM_SIZE}
                overscanCount={5}
                itemCount={itemCount}
                ref={gridRef}
              >
                {renderRow}
              </FixedSizeList>
            </OuterElementContext.Provider>
          </div>
        );
      });
      VirtualizedListbox.displayName = "ListboxComponent";
      ListboxComponent = VirtualizedListbox;
    }

    return (
      <Autocomplete
        {...rest}
        disabled={isDisabled ?? false}
        options={availableOptions}
        value={field.value || (rest?.multiple ? [] : null)}
        {...(isVirtualized && ListboxComponent
          ? { ListboxComponent: ListboxComponent as any }
          : {})}
        popupIcon={
          <ArrowIcon style={{ rotate: "90deg" }} height={16} width={16} />
        }
        disableListWrap={isVirtualized}
        onInputChange={(_, newInputValue, reason) => {
          // Track search input to auto-expand matching parents
          if (reason === "input") {
            setSearchInput(newInputValue);
          } else if (reason === "clear" || reason === "reset") {
            setSearchInput("");
          }
          // Call original onInputChange if provided
          if (rest.onInputChange) {
            rest.onInputChange(_, newInputValue, reason);
          }
        }}
        onChange={(_, newValue) => {
          // Only allow selection of options without children (parent options are not selectable)
          if (
            newValue &&
            typeof newValue === "object" &&
            "hasChildren" in newValue
          ) {
            const treeOption = newValue as TreeOption;
            if (treeOption.hasChildren) {
              // Prevent selection of parent options
              return;
            }
          }
          onChange?.(newValue);
          field.onChange(newValue);
        }}
        disableClearable={false}
        getOptionLabel={(option: TreeOption) => option.label || ""}
        isOptionEqualToValue={(
          option: TreeOption,
          value: TreeOption | Option,
        ) => {
          // Compare with all selectable options to handle nested structure
          const optionValue = option.value;
          if (typeof value === "object" && value !== null) {
            return optionValue === (value as Option).value;
          }
          return false;
        }}
        filterOptions={(optionsToFilter, state) => {
          const inputValue = state.inputValue.toLowerCase().trim();

          if (!inputValue) {
            // No search - return all options as-is
            return optionsToFilter;
          }

          // Filter options that match the search
          const filtered: TreeOption[] = [];

          for (const option of optionsToFilter) {
            const matchesLabel = option.label
              .toLowerCase()
              .includes(inputValue);

            // Include parent options if they match or if any of their children match
            if (option.hasChildren) {
              // Check if any child matches (children are already expanded if they match)
              const hasMatchingChild = option.children?.some(
                (child: Option) => {
                  const childMatches = child.label
                    .toLowerCase()
                    .includes(inputValue);
                  // Also check nested children recursively
                  if (child.children && child.children.length > 0) {
                    return (
                      childMatches ||
                      child.children.some((nested: Option) =>
                        nested.label.toLowerCase().includes(inputValue),
                      )
                    );
                  }
                  return childMatches;
                },
              );

              if (matchesLabel || hasMatchingChild) {
                filtered.push(option);
              }
            } else {
              // Leaf node - include if it matches
              if (matchesLabel) {
                filtered.push(option);
              }
            }
          }

          return filtered;
        }}
        slotProps={{
          ...callerSlotProps,
          listbox: {
            ...callerSlotProps.listbox,
            sx: {
              ...(optionsHeight
                ? { maxHeight: optionsHeight }
                : callerSlotProps.listbox?.sx),
              padding: 0,
              margin: 0,
              boxSizing: "border-box",
            },
          },
        }}
        renderOption={(props, option: TreeOption) => {
          const level = option.level || 0;
          const paddingLeft = level * 24; // 24px per level
          const hasChildren = option.hasChildren;
          const isExpanded = expandedSet.has(option.value);

          // If option has children, it's not selectable - only for display/expand
          const isSelectable = !hasChildren;

          return (
            <Box
              component="li"
              {...props}
              sx={{
                paddingLeft: `${8 + paddingLeft}px !important`,
                fontWeight: level === 0 ? 500 : 400,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                cursor: isSelectable ? "pointer" : "default",
                opacity: isSelectable ? 1 : 0.9,
                "&:hover": {
                  backgroundColor: isSelectable
                    ? "rgba(0, 0, 0, 0.04)"
                    : "rgba(0, 0, 0, 0.02)",
                },
              }}
              onClick={(e) => {
                // If it has children, prevent selection and only allow expand/collapse
                if (hasChildren) {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleExpand(option.value, e);
                } else {
                  // Allow normal selection for leaf nodes
                  props.onClick?.(e);
                }
              }}
            >
              <span style={{ flex: 1 }}>{option.label}</span>
              {hasChildren && (
                <IconButton
                  size="small"
                  onClick={(e) => toggleExpand(option.value, e)}
                  sx={{
                    padding: "4px",
                    marginRight: "4px",
                    "&:hover": {
                      backgroundColor: "rgba(0, 0, 0, 0.08)",
                    },
                  }}
                >
                  <ArrowIcon
                    width={16}
                    height={16}
                    style={{
                      transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
                      transition: "transform 0.2s ease",
                    }}
                  />
                </IconButton>
              )}
            </Box>
          );
        }}
        renderInput={(params) => {
          const callerInputSlot = callerSlotProps.input ?? {};
          const mergedInputProps = {
            ...params.InputProps,
            ...(callerInputSlot.InputProps ?? {}),
          };
          const callerStartAdornment =
            callerInputSlot?.InputProps?.startAdornment ?? null;

          const startAdornment = (
            <>
              {callerStartAdornment}
              {startIcon && (
                <InputAdornment position="start">{startIcon}</InputAdornment>
              )}
              {params.InputProps?.startAdornment}
            </>
          );
          const mergedHtmlInputProps = {
            ...params.inputProps,
            ...(callerInputSlot.inputProps ?? {}),
            className: combineClasses(
              params.inputProps?.className || undefined,
              callerInputSlot.inputProps?.className || undefined,
              styles.inputField,
              startIcon ? styles.startIcon : undefined,
            ),
          };

          return (
            <TextField
              {...params}
              placeholder={placeholder}
              className={combineClasses(
                styles.inputRoot,
                error ? styles.error : undefined,
                startIcon ? styles.startIcon : undefined,
                rest?.className || undefined,
              )}
              InputProps={{
                ...mergedInputProps,
                startAdornment,
              }}
              inputProps={mergedHtmlInputProps}
              variant="outlined"
              size="small"
            />
          );
        }}
      />
    );
  };

  if (control) {
    return (
      <Controller
        control={control}
        {...(validations ? { rules: validations } : {})}
        name={name}
        render={({ field }) => renderInput(field)}
      />
    );
  } else {
    return renderInput({
      value: rest?.value,
      onChange: onChange || (() => {}),
      name,
    });
  }
};

const SelectWithLabelAndError = withLabelAndError(
  Select as WithLabelAndErrorComponent,
);

export default SelectWithLabelAndError;
export { Select };
